import { useEffect, useMemo, useState } from "react";
import { api, authedUrl } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Check, Crown, LogOut, Pencil, UserPlus, X } from "lucide-react";
import { toast } from "sonner";

const initialsOf = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

function PersonRow({ person, checked, onClick, right, testid }) {
  return (
    <div
      role={onClick ? "button" : undefined}
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 text-sm transition-colors ${onClick ? "cursor-pointer" : ""} ${checked ? "bg-primary/15" : onClick ? "hover:bg-accent" : ""}`}
    >
      <Avatar className="h-7 w-7"><AvatarImage src={authedUrl(person.avatar_url)} /><AvatarFallback className="text-[9px]">{initialsOf(person.full_name)}</AvatarFallback></Avatar>
      <div className="flex-1 min-w-0">
        <div className="truncate">{person.full_name}</div>
        {person.designation && <div className="text-[10px] text-muted-foreground truncate">{person.designation}</div>}
      </div>
      {right}
      {checked && <Check className="w-4 h-4 text-primary" />}
    </div>
  );
}

/** Multi-select list of people with a search box. */
function MemberPicker({ people, selected, onToggle }) {
  const [q, setQ] = useState("");
  const filtered = people.filter((p) => !q || p.full_name.toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <Input data-testid="group-member-search" placeholder="Search people…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" />
      <div className="max-h-60 overflow-y-auto scroll-thin space-y-1">
        {filtered.map((p) => (
          <PersonRow key={p.id} person={p} checked={selected.has(p.id)} onClick={() => onToggle(p.id)} testid={`group-pick-${p.id}`} />
        ))}
        {filtered.length === 0 && <div className="text-xs text-muted-foreground p-3 text-center">No people found</div>}
      </div>
    </>
  );
}

export function CreateGroupDialog({ open, onClose, contacts, onCreated }) {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setName(""); setSelected(new Set()); } }, [open]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const create = async () => {
    if (!name.trim()) return toast.error("Give the group a name");
    if (selected.size === 0) return toast.error("Add at least one member");
    setSaving(true);
    try {
      const { data } = await api.post("/chat/groups", { name: name.trim(), member_ids: Array.from(selected) });
      toast.success(`Group "${data.title}" created`);
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create group");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="create-group-dialog">
        <DialogHeader>
          <DialogTitle>New group</DialogTitle>
          <DialogDescription>Name the group and pick who to add. You can change members later.</DialogDescription>
        </DialogHeader>
        <Input data-testid="group-name-input" placeholder="Group name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        <MemberPicker people={contacts} selected={selected} onToggle={toggle} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button data-testid="group-create-btn" onClick={create} disabled={saving || !name.trim() || selected.size === 0}>
            {saving ? "Creating…" : `Create${selected.size ? ` (${selected.size})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Group info: members list, rename / add / remove (group admin), leave (anyone). */
export function GroupInfoDialog({ open, onClose, conv, user, contacts, onChanged, onLeft }) {
  const [members, setMembers] = useState([]);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const canManage = user.role === "admin" || conv?.created_by === user.id;

  const load = () => api.get(`/chat/groups/${conv.id}/members`).then((r) => setMembers(r.data)).catch(() => {});
  useEffect(() => {
    if (open && conv) { load(); setName(conv.title || ""); setRenaming(false); setAdding(false); setSelected(new Set()); }
    // eslint-disable-next-line
  }, [open, conv?.id]);

  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const addable = contacts.filter((c) => !memberIds.has(c.id));

  const call = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const rename = () => call(async () => {
    await api.patch(`/chat/groups/${conv.id}`, { name });
    setRenaming(false);
  }, "Group renamed");

  const addMembers = () => call(async () => {
    await api.post(`/chat/groups/${conv.id}/members`, { member_ids: Array.from(selected) });
    setAdding(false); setSelected(new Set());
  }, "Members added");

  const remove = (m) => {
    if (!window.confirm(`Remove ${m.full_name} from the group?`)) return;
    call(() => api.delete(`/chat/groups/${conv.id}/members/${m.id}`), `${m.full_name} removed`);
  };

  const leave = async () => {
    if (!window.confirm("Leave this group?")) return;
    try {
      await api.delete(`/chat/groups/${conv.id}/members/${user.id}`);
      onLeft?.();
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not leave"); }
  };

  if (!conv) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="group-info-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {renaming ? (
              <>
                <Input data-testid="group-rename-input" value={name} onChange={(e) => setName(e.target.value)} className="h-8" maxLength={80} />
                <Button size="sm" data-testid="group-rename-save" onClick={rename} disabled={!name.trim()}>Save</Button>
                <Button size="icon" variant="ghost" onClick={() => setRenaming(false)}><X className="w-4 h-4" /></Button>
              </>
            ) : (
              <>
                <span className="truncate" data-testid="group-info-title">{conv.title}</span>
                {canManage && <Button size="icon" variant="ghost" data-testid="group-rename-btn" onClick={() => setRenaming(true)}><Pencil className="w-3.5 h-3.5" /></Button>}
              </>
            )}
          </DialogTitle>
          <DialogDescription>{members.length} member{members.length === 1 ? "" : "s"}</DialogDescription>
        </DialogHeader>

        {adding ? (
          <>
            <MemberPicker people={addable} selected={selected} onToggle={(id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; })} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdding(false)}>Back</Button>
              <Button data-testid="group-add-confirm" onClick={addMembers} disabled={selected.size === 0}>Add{selected.size ? ` (${selected.size})` : ""}</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            {canManage && (
              <Button variant="outline" size="sm" data-testid="group-add-members" onClick={() => setAdding(true)} disabled={addable.length === 0}>
                <UserPlus className="w-4 h-4 mr-1" />Add members
              </Button>
            )}
            <div className="max-h-64 overflow-y-auto scroll-thin space-y-1">
              {members.map((m) => (
                <PersonRow
                  key={m.id}
                  person={m}
                  testid={`group-member-${m.id}`}
                  right={
                    <>
                      {m.is_group_admin && <span className="text-[10px] text-primary flex items-center gap-0.5"><Crown className="w-3 h-3" />Admin</span>}
                      {canManage && !m.is_group_admin && m.id !== user.id && (
                        <Button size="icon" variant="ghost" className="h-6 w-6" data-testid={`group-remove-${m.id}`} onClick={() => remove(m)} title="Remove">
                          <X className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      )}
                    </>
                  }
                />
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" className="text-red-500" data-testid="group-leave" onClick={leave}><LogOut className="w-4 h-4 mr-1" />Leave group</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
